import { cloneDeep } from 'lodash';
import {
    CITATION_SELECT_OPTIONS,
    DEFAULT_FEEDBACK_LANGUAGE_LEVEL,
    DEFAULT_FEEDBACK_NUM_SUGGESTIONS,
    DEFAULT_FEEDBACK_STRUCTURE,
    DEFAULT_FEEDBACK_SUGGESTION_LENGTH,
    DEFAULT_FEEDBACK_TONE,
    DEFAULT_INLINE_COMMENTS_COMMENT_LENGTH,
    DEFAULT_INLINE_COMMENTS_LANGUAGE_LEVEL,
    DEFAULT_INLINE_COMMENTS_MAX_COMMENTS_PER_PAGE,
    DEFAULT_INLINE_COMMENTS_TONE,
} from 'utils/constants';
import { normalizeRubricScoringMode } from 'utils/rubricScoring';
import { v4 as uuid } from 'uuid';

export const SYSTEM_ASSIGNMENT_DEFAULTS = {
    aiGradingEnabled: true,
    gradingCustomContext: '',
    aiFeedbackEnabled: true,
    feedbackSettings: {
        structure: DEFAULT_FEEDBACK_STRUCTURE,
        numSuggestions: DEFAULT_FEEDBACK_NUM_SUGGESTIONS,
        suggestionLength: DEFAULT_FEEDBACK_SUGGESTION_LENGTH,
        languageLevel: DEFAULT_FEEDBACK_LANGUAGE_LEVEL,
        tone: DEFAULT_FEEDBACK_TONE,
        templated: {},
        customInstructions: '',
    },
    feedbackCustomContext: '',
    aiStudentFeedbackEnabled: false,
    studentFeedbackSettings: {
        structure: DEFAULT_FEEDBACK_STRUCTURE,
        numSuggestions: DEFAULT_FEEDBACK_NUM_SUGGESTIONS,
        suggestionLength: DEFAULT_FEEDBACK_SUGGESTION_LENGTH,
        languageLevel: DEFAULT_FEEDBACK_LANGUAGE_LEVEL,
        tone: DEFAULT_FEEDBACK_TONE,
        templated: {},
        customInstructions: '',
    },
    studentFeedbackCustomContext: '',
    aiInlineCommentsEnabled: true,
    inlineCommentsSettings: {
        maxCommentsPerPage: DEFAULT_INLINE_COMMENTS_MAX_COMMENTS_PER_PAGE,
        commentLength: DEFAULT_INLINE_COMMENTS_COMMENT_LENGTH,
        languageLevel: DEFAULT_INLINE_COMMENTS_LANGUAGE_LEVEL,
        tone: DEFAULT_INLINE_COMMENTS_TONE,
    },
    inlineCommentsCustomContext: '',
};

const merge = (base, value) => {
    if (!value || typeof value !== 'object' || Array.isArray(value)) return base;
    return Object.keys(base).reduce((result, key) => {
        const next = value[key];
        result[key] =
            next && typeof next === 'object' && !Array.isArray(next)
                ? merge(base[key], next)
                : next === undefined
                  ? base[key]
                  : next;
        return result;
    }, {});
};

const mergeAssignmentDefaults = (defaults) => merge(SYSTEM_ASSIGNMENT_DEFAULTS, defaults);

const feedbackSettingsFromApi = (settings, defaults) => {
    const {
        num_suggestions,
        suggestion_length,
        language_level,
        custom_instructions,
        numSuggestions: ignoredNumSuggestions,
        suggestionLength: ignoredSuggestionLength,
        languageLevel: ignoredLanguageLevel,
        customInstructions: ignoredCustomInstructions,
        ...extras
    } = cloneDeep(settings || {});
    return {
        ...cloneDeep(defaults),
        ...extras,
        numSuggestions: num_suggestions ?? defaults.numSuggestions,
        suggestionLength: suggestion_length ?? defaults.suggestionLength,
        languageLevel: language_level ?? defaults.languageLevel,
        customInstructions: custom_instructions ?? defaults.customInstructions,
    };
};

const inlineCommentsSettingsFromApi = (settings) => {
    const {
        max_comments_per_page,
        comment_length,
        language_level,
        maxCommentsPerPage: ignoredMaxCommentsPerPage,
        commentLength: ignoredCommentLength,
        languageLevel: ignoredLanguageLevel,
        ...extras
    } = cloneDeep(settings || {});
    return {
        ...cloneDeep(SYSTEM_ASSIGNMENT_DEFAULTS.inlineCommentsSettings),
        ...extras,
        maxCommentsPerPage:
            max_comments_per_page ?? SYSTEM_ASSIGNMENT_DEFAULTS.inlineCommentsSettings.maxCommentsPerPage,
        commentLength: comment_length ?? SYSTEM_ASSIGNMENT_DEFAULTS.inlineCommentsSettings.commentLength,
        languageLevel: language_level ?? SYSTEM_ASSIGNMENT_DEFAULTS.inlineCommentsSettings.languageLevel,
    };
};

export const createAssignmentForm = (overrides = {}) => ({
    title: '',
    description: '',
    rubric: null,
    citationFormat: CITATION_SELECT_OPTIONS[0].key,
    contentBankResources: [],
    ...cloneDeep(SYSTEM_ASSIGNMENT_DEFAULTS),
    ...cloneDeep(overrides),
});

export const assignmentFormFromApi = (assignment = {}) =>
    createAssignmentForm({
        title: assignment.title ?? '',
        description: assignment.description ?? '',
        rubric: assignment.rubric ? normalizeRubricScoringMode(assignment) : null,
        citationFormat: assignment.citation_format ?? CITATION_SELECT_OPTIONS[0].key,
        contentBankResources: cloneDeep(assignment.content_bank_resources || []),
        aiGradingEnabled: assignment.ai_grading_enabled ?? SYSTEM_ASSIGNMENT_DEFAULTS.aiGradingEnabled,
        gradingSettings: cloneDeep(assignment.ai_grading_settings),
        gradingCustomContext: assignment.ai_grading_context ?? '',
        aiFeedbackEnabled: assignment.ai_feedback_enabled ?? SYSTEM_ASSIGNMENT_DEFAULTS.aiFeedbackEnabled,
        feedbackSettings: feedbackSettingsFromApi(
            assignment.ai_feedback_settings,
            SYSTEM_ASSIGNMENT_DEFAULTS.feedbackSettings
        ),
        feedbackCustomContext: assignment.ai_feedback_context ?? '',
        aiStudentFeedbackEnabled:
            assignment.ai_student_feedback_enabled ?? SYSTEM_ASSIGNMENT_DEFAULTS.aiStudentFeedbackEnabled,
        studentFeedbackSettings: feedbackSettingsFromApi(
            assignment.ai_student_feedback_settings,
            SYSTEM_ASSIGNMENT_DEFAULTS.studentFeedbackSettings
        ),
        studentFeedbackCustomContext: assignment.ai_student_feedback_context ?? '',
        aiInlineCommentsEnabled:
            assignment.ai_inline_comments_enabled ?? SYSTEM_ASSIGNMENT_DEFAULTS.aiInlineCommentsEnabled,
        inlineCommentsSettings: inlineCommentsSettingsFromApi(assignment.ai_inline_comments_settings),
        inlineCommentsCustomContext: assignment.ai_inline_comments_context ?? '',
        ...(assignment.api_assignment_id && {
            api_assignment_id: assignment.api_assignment_id,
            api_data: assignment.api_data,
        }),
    });

export const assignmentImportPatch = (assignment = {}) => ({
    title: assignment.title ?? '',
    description: assignment.description ?? '',
    rubric: cloneDeep(assignment.rubric || null),
    citationFormat: assignment.citationFormat ?? CITATION_SELECT_OPTIONS[0].key,
    contentBankResources: cloneDeep(assignment.contentBankResources || []),
    api_assignment_id: assignment.api_assignment_id,
    api_data: assignment.api_data,
});

export const assignmentDefaultsToApi = (form) => {
    const {
        structure,
        numSuggestions,
        suggestionLength,
        languageLevel,
        tone,
        customInstructions,
        templated,
        ...feedbackExtras
    } = form.feedbackSettings || {};
    const {
        structure: studentStructure,
        numSuggestions: studentNumSuggestions,
        suggestionLength: studentSuggestionLength,
        languageLevel: studentLanguageLevel,
        tone: studentTone,
        customInstructions: studentCustomInstructions,
        templated: studentTemplated,
        ...studentFeedbackExtras
    } = form.studentFeedbackSettings || {};
    const {
        maxCommentsPerPage,
        commentLength,
        languageLevel: inlineLanguageLevel,
        tone: inlineTone,
        ...inlineCommentsExtras
    } = form.inlineCommentsSettings || {};
    return {
        ai_grading_enabled: Boolean(form.aiGradingEnabled),
        ai_grading_settings: cloneDeep(form.gradingSettings),
        ai_grading_context: form.gradingCustomContext || '',
        ai_feedback_enabled: Boolean(form.aiFeedbackEnabled),
        ai_feedback_settings: {
            ...cloneDeep(feedbackExtras),
            structure,
            num_suggestions: numSuggestions,
            suggestion_length: suggestionLength,
            language_level: languageLevel,
            tone,
            templated: cloneDeep(templated),
            custom_instructions: customInstructions || '',
        },
        ai_feedback_context: form.feedbackCustomContext || '',
        ai_student_feedback_enabled: Boolean(form.aiStudentFeedbackEnabled),
        ai_student_feedback_settings: {
            ...cloneDeep(studentFeedbackExtras),
            structure: studentStructure,
            num_suggestions: studentNumSuggestions,
            suggestion_length: studentSuggestionLength,
            language_level: studentLanguageLevel,
            tone: studentTone,
            templated: cloneDeep(studentTemplated),
            custom_instructions: studentCustomInstructions || '',
        },
        ai_student_feedback_context: form.studentFeedbackCustomContext || '',
        ai_inline_comments_enabled: Boolean(form.aiInlineCommentsEnabled),
        ai_inline_comments_settings: {
            ...cloneDeep(inlineCommentsExtras),
            max_comments_per_page: maxCommentsPerPage,
            comment_length: commentLength,
            language_level: inlineLanguageLevel,
            tone: inlineTone,
        },
        ai_inline_comments_context: form.inlineCommentsCustomContext || '',
    };
};

export const assignmentFormToApi = (
    form,
    {
        mode = 'create',
        courseId,
        ownerAccountId,
        assignmentId,
        apiData,
        rubric,
        isDraft = false,
        existing = {},
        restrictions = {},
    } = {}
) => {
    const timestamp = Date.now();
    const settings = assignmentDefaultsToApi({
        ...form,
        aiGradingEnabled: form.aiGradingEnabled && !restrictions.isGradingFeatureDisabled,
        aiFeedbackEnabled: form.aiFeedbackEnabled && !restrictions.isFeedbackFeatureDisabled,
        aiInlineCommentsEnabled: form.aiInlineCommentsEnabled && !restrictions.isInlineCommentsFeatureDisabled,
    });
    const result = {
        ...(mode === 'create' && {
            id: assignmentId || uuid(),
            active: true,
            owner_account_id: ownerAccountId,
            created_on: timestamp,
        }),
        modified_on: timestamp,
        course_id: courseId,
        title: form.title,
        description: form.description,
        rubric: rubric ?? form.rubric,
        citation_format: form.citationFormat,
        content_bank_resources: cloneDeep(form.contentBankResources || []),
        ...settings,
        is_draft: isDraft,
        has_seen_preview: existing.has_seen_preview ?? false,
        has_finished_preview: existing.has_finished_preview ?? false,
        preview_files: cloneDeep(existing.preview_files || []),
    };
    if (form.api_assignment_id) {
        result.api_assignment_id = form.api_assignment_id;
        result.api_data = { ...(apiData || form.api_data || {}), id: form.api_assignment_id };
    }
    return result;
};

export const assignmentDefaultsFromApi = (defaults) => {
    const value = mergeAssignmentDefaults({
        aiGradingEnabled: defaults?.ai_grading_enabled,
        gradingCustomContext: defaults?.ai_grading_context,
        aiFeedbackEnabled: defaults?.ai_feedback_enabled,
        feedbackSettings: {
            structure: defaults?.ai_feedback_settings?.structure,
            numSuggestions: defaults?.ai_feedback_settings?.num_suggestions,
            suggestionLength: defaults?.ai_feedback_settings?.suggestion_length,
            languageLevel: defaults?.ai_feedback_settings?.language_level,
            tone: defaults?.ai_feedback_settings?.tone,
            customInstructions: defaults?.ai_feedback_settings?.custom_instructions,
        },
        feedbackCustomContext: defaults?.ai_feedback_context,
        aiStudentFeedbackEnabled: defaults?.ai_student_feedback_enabled,
        studentFeedbackSettings: {
            structure: defaults?.ai_student_feedback_settings?.structure,
            numSuggestions: defaults?.ai_student_feedback_settings?.num_suggestions,
            suggestionLength: defaults?.ai_student_feedback_settings?.suggestion_length,
            languageLevel: defaults?.ai_student_feedback_settings?.language_level,
            tone: defaults?.ai_student_feedback_settings?.tone,
            customInstructions: defaults?.ai_student_feedback_settings?.custom_instructions,
        },
        studentFeedbackCustomContext: defaults?.ai_student_feedback_context,
        aiInlineCommentsEnabled: defaults?.ai_inline_comments_enabled,
        inlineCommentsSettings: {
            maxCommentsPerPage: defaults?.ai_inline_comments_settings?.max_comments_per_page,
            commentLength: defaults?.ai_inline_comments_settings?.comment_length,
            languageLevel: defaults?.ai_inline_comments_settings?.language_level,
            tone: defaults?.ai_inline_comments_settings?.tone,
        },
        inlineCommentsCustomContext: defaults?.ai_inline_comments_context,
    });
    return value;
};
